import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Download, IndianRupee, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';
import ExportMenu from '../components/ExportMenu';

const FY_OPTIONS = ['2023-24', '2024-25', '2025-26', '2026-27'];
const ITR_TYPES = ['ITR-1', 'ITR-2', 'ITR-3', 'ITR-4', 'ITR-5', 'ITR-6', 'ITR-7'];
const EVC_STATUSES = ['Evc done', 'Tax Due', 'Pending'];
const PAYMENT_MODES = ['Cash', 'UPI to sir', 'UPI to SVT', 'Cash sir'];
const FILING_STATUSES = ['Pending', 'Filed', 'Revised'];

const PAYMENT_COLORS = {
  'Cash': 'bg-green-600 text-white',
  'UPI to sir': 'bg-blue-600 text-white',
  'UPI to SVT': 'bg-indigo-600 text-white',
  'Cash sir': 'bg-teal-600 text-white',
};

const EVC_COLORS = {
  'Evc done': 'text-green-700',
  'Tax Due': 'text-red-600 font-semibold',
  'Pending': 'text-yellow-600',
};

export default function IncomeTaxPage() {
  const [records, setRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [totalData, setTotalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [fyFilter, setFyFilter] = useState('2025-26');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [form, setForm] = useState({
    client_id: '', client_name: '', contact_person: '',
    financial_year: '2025-26', itr_type: '', filing_date: '',
    acknowledgement_number: '', evc_status: 'Pending',
    fees_collected: 0, payment_mode: '', status: 'Pending',
    sir_notes: '', gm_notes: '', jayashree_notes: '', notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (fyFilter) params.set('financial_year', fyFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (searchText) params.set('search', searchText);
      const [recRes, clRes, totalRes] = await Promise.all([
        api.get(`/income-tax?${params}`),
        api.get('/clients?limit=500'),
        api.get(`/income-tax/total-collection?financial_year=${fyFilter}`)
      ]);
      setRecords(recRes.data);
      setClients(clRes.data.clients || []);
      setTotalData(totalRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [fyFilter, statusFilter, searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditId(null);
    setForm({
      client_id: '', client_name: '', contact_person: '',
      financial_year: fyFilter || '2025-26', itr_type: '', filing_date: '',
      acknowledgement_number: '', evc_status: 'Pending',
      fees_collected: 0, payment_mode: '', status: 'Pending',
      sir_notes: '', gm_notes: '', jayashree_notes: '', notes: ''
    });
    setDialogOpen(true);
  };

  const openEdit = (rec) => {
    setEditId(rec._id);
    setForm({
      client_id: rec.client_id, client_name: rec.client_name,
      contact_person: rec.contact_person || '',
      financial_year: rec.financial_year, itr_type: rec.itr_type || '',
      filing_date: rec.filing_date || '',
      acknowledgement_number: rec.acknowledgement_number || '',
      evc_status: rec.evc_status || 'Pending',
      fees_collected: rec.fees_collected || 0,
      payment_mode: rec.payment_mode || '',
      status: rec.status, sir_notes: rec.sir_notes || '',
      gm_notes: rec.gm_notes || '', jayashree_notes: rec.jayashree_notes || '',
      notes: rec.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.client_name) { toast.error('Client name is required'); return; }
    try {
      if (editId) {
        await api.put(`/income-tax/${editId}`, form);
        toast.success('Record updated');
      } else {
        await api.post('/income-tax', form);
        toast.success('Record created');
      }
      setDialogOpen(false);
      fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try { await api.delete(`/income-tax/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  // Inline edit for quick updates
  const quickUpdate = async (id, field, value) => {
    try {
      await api.put(`/income-tax/${id}`, { [field]: value });
      setRecords(prev => prev.map(r => r._id === id ? { ...r, [field]: value } : r));
      if (field === 'fees_collected' || field === 'payment_mode') fetchData(); // refresh totals
    } catch { toast.error('Update failed'); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
  };

  return (
    <div data-testid="income-tax-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Income Tax</h1>
          <p className="text-sm text-[#4F734F] mt-1">Track ITR filings, fees, and collection status</p>
        </div>
        <div className="flex gap-2">
          <ExportMenu
            endpoint="/exports/income-tax"
            params={{ ...(fyFilter ? { financial_year: fyFilter } : {}), ...(statusFilter ? { status: statusFilter } : {}), ...(searchText ? { search: searchText } : {}) }}
            filename="income_tax"
            testIdPrefix="itr"
          />
          <Button onClick={openNew} className="btn-accent gap-2" data-testid="add-itr-btn">
            <Plus size={16} /> New Filing
          </Button>
        </div>
      </div>

      {/* Total Collection Summary */}
      {totalData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="stat-card" data-testid="total-collection-card">
            <div className="flex items-center gap-2 mb-1">
              <IndianRupee size={16} className="text-[#D4AF37]" />
              <p className="text-xs uppercase font-semibold text-[#4F734F]">Total Collection</p>
            </div>
            <p className="font-heading text-3xl font-light text-[#111B11]">
              {(totalData.total_collection || 0).toLocaleString('en-IN')}
            </p>
            <p className="text-xs text-olive-500 mt-1">{totalData.total_clients || 0} clients for FY {fyFilter}</p>
          </div>
          {Object.entries(totalData.by_payment_mode || {}).map(([mode, amount]) => (
            <div key={mode} className="stat-card">
              <p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">{mode}</p>
              <p className="font-heading text-2xl font-light text-[#111B11]">
                {amount.toLocaleString('en-IN')}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search client, ACK no..."
          testId="itr-search"
          minWidth="min-w-[280px]"
          className="!flex-none w-full sm:w-80"
        />
        <Select value={fyFilter || 'all'} onValueChange={(v) => setFyFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36 h-11 input-field" data-testid="fy-filter"><SelectValue placeholder="All FY" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All FY</SelectItem>
            {FY_OPTIONS.map(fy => <SelectItem key={fy} value={fy}>{fy}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36 h-11 input-field" data-testid="itr-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {FILING_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Main Data Table - Spreadsheet Style */}
      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left" data-testid="income-tax-table">
            <thead>
              <tr className="bg-[#1B2A1B] text-white">
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider w-10">SL</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider min-w-[180px]">Name</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider min-w-[120px]">Contact Person</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider w-[100px]">Date of Filing</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider min-w-[150px]">ACK NO</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider w-[90px]">EVC</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider w-[80px] text-right">Fees</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider w-[100px]">Payment</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider min-w-[160px]">Sir Notes</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider min-w-[120px]">GM Notes</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider min-w-[110px]">Jayashree Notes</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider w-[70px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center py-8 text-olive-400">Loading...</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-8 text-olive-400">No records found for FY {fyFilter}</td></tr>
              ) : records.map((rec, idx) => (
                <tr key={rec._id} className="border-b border-[#E6EDE6] hover:bg-[#F8FAF7] transition-colors" data-testid={`itr-row-${rec._id}`}>
                  <td className="py-2 px-3 text-olive-500 font-mono text-xs">{idx + 1}</td>
                  <td className="py-2 px-3 font-medium text-[#111B11]">{rec.client_name}</td>
                  <td className="py-2 px-3 text-[#3B5A3B]">{rec.contact_person || '-'}</td>
                  <td className="py-2 px-3 text-[#273A27]">{formatDate(rec.filing_date)}</td>
                  <td className="py-2 px-3 font-mono text-xs text-[#273A27]">{rec.acknowledgement_number || '-'}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1">
                      {rec.evc_status === 'Evc done' && <CheckCircle2 size={13} className="text-green-600" />}
                      {rec.evc_status === 'Tax Due' && <AlertCircle size={13} className="text-red-500" />}
                      <span className={`text-xs ${EVC_COLORS[rec.evc_status] || 'text-olive-500'}`}>
                        {rec.evc_status || 'Pending'}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right font-mono font-medium text-[#111B11]">
                    {rec.fees_collected ? rec.fees_collected.toLocaleString('en-IN') : '-'}
                  </td>
                  <td className="py-2 px-3">
                    {rec.payment_mode ? (
                      <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${PAYMENT_COLORS[rec.payment_mode] || 'bg-gray-200 text-gray-700'}`}>
                        {rec.payment_mode}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="py-2 px-3 text-xs text-[#3B5A3B] max-w-[200px]">
                    <span className="line-clamp-2">{rec.sir_notes || '-'}</span>
                  </td>
                  <td className="py-2 px-3 text-xs text-[#3B5A3B] max-w-[160px]">
                    <span className="line-clamp-2">{rec.gm_notes || '-'}</span>
                  </td>
                  <td className="py-2 px-3">
                    {rec.jayashree_notes ? (
                      <span className={`text-xs font-medium ${rec.jayashree_notes === 'FILED DONE' ? 'text-green-700' : 'text-[#3B5A3B]'}`}>
                        {rec.jayashree_notes}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex gap-0.5">
                      <button onClick={() => openEdit(rec)} className="p-1 hover:bg-olive-100 rounded-sm" data-testid={`edit-itr-${rec._id}`}>
                        <Pencil size={13} className="text-olive-500" />
                      </button>
                      <button onClick={() => handleDelete(rec._id)} className="p-1 hover:bg-red-50 rounded-sm" data-testid={`delete-itr-${rec._id}`}>
                        <Trash2 size={13} className="text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {/* Totals Row */}
              {records.length > 0 && (
                <tr className="bg-[#F4F7F4] border-t-2 border-[#CCD9CC] font-semibold">
                  <td colSpan={6} className="py-2.5 px-3 text-right text-xs uppercase tracking-wider text-[#4F734F]">
                    Total Collection ({records.length} clients)
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-[#111B11] text-base">
                    {records.reduce((sum, r) => sum + (r.fees_collected || 0), 0).toLocaleString('en-IN')}
                  </td>
                  <td colSpan={5}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'New'} Income Tax Filing</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Row 1: Client & Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Client Name</Label>
                <Select value={form.client_id || 'none'} onValueChange={(v) => {
                  const c = clients.find(cl => cl._id === v);
                  setForm(prev => ({ ...prev, client_id: v, client_name: c?.full_name || '' }));
                }}>
                  <SelectTrigger className="input-field" data-testid="itr-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Select client</SelectItem>
                    {clients.map(c => <SelectItem key={c._id} value={c._id}>{c.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {/* Manual name entry fallback */}
                <Input
                  value={form.client_name}
                  onChange={(e) => setForm(prev => ({ ...prev, client_name: e.target.value }))}
                  className="input-field mt-1"
                  placeholder="Or type client name directly"
                  data-testid="itr-client-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Contact Person</Label>
                <Input
                  value={form.contact_person}
                  onChange={(e) => setForm(prev => ({ ...prev, contact_person: e.target.value }))}
                  className="input-field"
                  placeholder="Contact person name"
                  data-testid="itr-contact-input"
                />
              </div>
            </div>

            {/* Row 2: FY, ITR Type, Filing Date */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Financial Year</Label>
                <Select value={form.financial_year} onValueChange={(v) => setForm(prev => ({ ...prev, financial_year: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent>{FY_OPTIONS.map(fy => <SelectItem key={fy} value={fy}>{fy}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">ITR Type</Label>
                <Select value={form.itr_type || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, itr_type: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="input-field"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{ITR_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Date of Filing</Label>
                <Input type="date" value={form.filing_date} onChange={(e) => setForm(prev => ({ ...prev, filing_date: e.target.value }))} className="input-field" data-testid="itr-filing-date" />
              </div>
            </div>

            {/* Row 3: ACK No, EVC, Status */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">ACK Number</Label>
                <Input
                  value={form.acknowledgement_number}
                  onChange={(e) => setForm(prev => ({ ...prev, acknowledgement_number: e.target.value }))}
                  className="input-field font-mono"
                  placeholder="Acknowledgement number"
                  data-testid="itr-ack-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">EVC Status</Label>
                <Select value={form.evc_status} onValueChange={(v) => setForm(prev => ({ ...prev, evc_status: v }))}>
                  <SelectTrigger className="input-field" data-testid="itr-evc-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{EVC_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Filing Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent>{FILING_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 4: Fees & Payment Mode */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Fees Collected (INR)</Label>
                <Input
                  type="number"
                  value={form.fees_collected}
                  onChange={(e) => setForm(prev => ({ ...prev, fees_collected: Number(e.target.value) }))}
                  className="input-field font-mono"
                  data-testid="itr-fees-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Payment Mode</Label>
                <Select value={form.payment_mode || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, payment_mode: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="input-field" data-testid="itr-payment-select"><SelectValue placeholder="Select mode" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select mode</SelectItem>
                    {PAYMENT_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 5: Notes */}
            <div className="border border-[#E6EDE6] rounded-sm p-4 space-y-3">
              <h3 className="text-xs uppercase font-semibold text-[#4F734F]">Notes</h3>
              <div className="grid grid-cols-1 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-[#4F734F]">Sir Notes</Label>
                  <Input
                    value={form.sir_notes}
                    onChange={(e) => setForm(prev => ({ ...prev, sir_notes: e.target.value }))}
                    className="input-field"
                    placeholder="Sir's notes..."
                    data-testid="itr-sir-notes"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#4F734F]">GM Notes</Label>
                  <Input
                    value={form.gm_notes}
                    onChange={(e) => setForm(prev => ({ ...prev, gm_notes: e.target.value }))}
                    className="input-field"
                    placeholder="GM notes..."
                    data-testid="itr-gm-notes"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#4F734F]">Jayashree Notes</Label>
                  <Select value={form.jayashree_notes || 'custom'} onValueChange={(v) => {
                    if (v !== 'custom') setForm(prev => ({ ...prev, jayashree_notes: v }));
                  }}>
                    <SelectTrigger className="input-field" data-testid="itr-jayashree-select"><SelectValue placeholder="Select or type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom text</SelectItem>
                      <SelectItem value="FILED DONE">FILED DONE</SelectItem>
                      <SelectItem value="PENDING">PENDING</SelectItem>
                      <SelectItem value="IN PROGRESS">IN PROGRESS</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={form.jayashree_notes}
                    onChange={(e) => setForm(prev => ({ ...prev, jayashree_notes: e.target.value }))}
                    className="input-field mt-1"
                    placeholder="Or type custom note..."
                    data-testid="itr-jayashree-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSubmit} className="btn-primary" data-testid="save-itr-btn">
                {editId ? 'Update' : 'Create'} Filing
              </Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
